import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  TextField,
  Grid,
  Paper,
  Card,
  CardContent,
  FormControlLabel,
  Checkbox,
  MenuItem,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  IconButton,
  Chip,
  Tooltip
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import api from '../axios';
import Header from '../components/Header';
import ContextNavBar from '../components/ContextNavBar';
import ModuleHeader from '../components/ModuleHeader';
import Tesseract from 'tesseract.js';
import {
  preprocessImage,
  extrairDadosInteligente,
  buscarEnderecoPorCEP,
  getCorConfianca
} from '../utils/ocrHelpers';
import { processarCardCascata } from '../utils/ocrIA';
import Footer from '../components/Footer';

const CapturarCard = ({ onLogout }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [imagemFile, setImagemFile] = useState(null);
  const [imagemPreview, setImagemPreview] = useState('');
  const [processandoOCR, setProcessandoOCR] = useState(false);
  const [progressoOCR, setProgressoOCR] = useState(0);
  const [textoExtraido, setTextoExtraido] = useState('');
  const [dadosConfianca, setDadosConfianca] = useState({});
  
  const [formData, setFormData] = useState({
    nome: '',
    sexo: '',
    telefone: '',
    email: '',
    whatsapp: '',
    data_nascimento: '',
    rua: '',
    bairro: '',
    cep: '',
    cidade: '',
    estado: '',
    quer_ser_membro: false,
    deseja_oracao: false,
    quer_conhecer_jesus: false,
    origem_cadastro: 'card_fisico'
  });

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [salvando, setSalvando] = useState(false);

  const steps = ['Upload da Imagem', 'Processar OCR', 'Revisar Dados', 'Confirmar'];

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setError('Por favor, selecione uma imagem válida');
        return;
      }
      
      setImagemFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagemPreview(reader.result);
      };
      reader.readAsDataURL(file);
      setActiveStep(1);
    }
  };

  const handleProcessarOCR = async () => {
    if (!imagemFile) {
      setError('Selecione uma imagem primeiro');
      return;
    }

    setProcessandoOCR(true);
    setProgressoOCR(0);
    setError('');

    try {
      // Usar sistema de cascata: IA → Tesseract multi-threshold
      const resultado = await processarCardCascata(
        imagemFile,
        api,
        Tesseract,
        (progresso) => setProgressoOCR(progresso)
      );

      if (resultado.sucesso) {
        // Se veio dados estruturados da IA
        if (resultado.dados && typeof resultado.dados === 'object') {
          preencherFormularioIA(resultado.dados);
          setTextoExtraido(JSON.stringify(resultado.dados, null, 2));
        } 
        // Se veio texto do Tesseract (fallback)
        else if (typeof resultado.dados === 'string') {
          setTextoExtraido(resultado.dados);
          await extrairDadosDoTextoInteligente(resultado.dados);
        }
        
        setProgressoOCR(100);
        setActiveStep(2);
      } else {
        throw new Error(resultado.erro || 'Falha no processamento');
      }
    } catch (error) {
      console.error('Erro no OCR:', error);
      setError('Erro ao processar imagem. Tente novamente ou preencha manualmente.');
    } finally {
      setProcessandoOCR(false);
    }
  };

  const extrairDadosDoTexto = (texto) => {
    const linhas = texto.split('\n').filter(l => l.trim());
    const dados = { ...formData };

    // Tentar extrair nome (geralmente na primeira linha)
    if (linhas.length > 0) {
      const primeiraLinha = linhas[0].trim();
      if (primeiraLinha.length > 2 && !primeiraLinha.match(/^\d/)) {
        dados.nome = primeiraLinha;
      }
    }

    // Procurar por telefone (padrões brasileiros)
    const regexTelefone = /(\(?\d{2}\)?\s?)?\d{4,5}[-\s]?\d{4}/g;
    const telefones = texto.match(regexTelefone);
    if (telefones && telefones.length > 0) {
      dados.telefone = telefones[0].replace(/\D/g, '');
      if (telefones.length > 1) {
        dados.whatsapp = telefones[1].replace(/\D/g, '');
      }
    }

    // Procurar por email
    const regexEmail = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
    const emails = texto.match(regexEmail);
    if (emails && emails.length > 0) {
      dados.email = emails[0].toLowerCase();
    }

    // Procurar por CEP
    const regexCep = /\d{5}[-]?\d{3}/g;
    const ceps = texto.match(regexCep);
    if (ceps && ceps.length > 0) {
      dados.cep = ceps[0].replace(/\D/g, '');
    }

    // Procurar por palavras-chave dos checkboxes
    const textoLower = texto.toLowerCase();
    if (textoLower.includes('membro') || textoLower.includes('batismo')) {
      dados.quer_ser_membro = true;
    }
    if (textoLower.includes('oração') || textoLower.includes('oracao') || textoLower.includes('orar')) {
      dados.deseja_oracao = true;
    }
    if (textoLower.includes('jesus') || textoLower.includes('salvação') || textoLower.includes('salvacao')) {
      dados.quer_conhecer_jesus = true;
    }

    // Detectar sexo por palavras-chave
    if (textoLower.includes('masculino') || textoLower.includes('homem') || textoLower.includes('sr.')) {
      dados.sexo = 'masculino';
    } else if (textoLower.includes('feminino') || textoLower.includes('mulher') || textoLower.includes('sra.')) {
      dados.sexo = 'feminino';
    }

    setFormData(dados);
  };

  const extrairDadosDoTextoInteligente = async (texto) => {
    // Usar extração inteligente com validação
    const dadosExtraidos = extrairDadosInteligente(texto);
    
    // Salvar níveis de confiança
    const confiancas = {};
    Object.keys(dadosExtraidos).forEach(key => {
      if (dadosExtraidos[key].confianca !== undefined) {
        confiancas[key] = dadosExtraidos[key].confianca;
      }
    });
    setDadosConfianca(confiancas);

    // Converter para formato do formulário
    const dados = {
      nome: dadosExtraidos.nome.valor,
      sexo: dadosExtraidos.sexo.valor,
      telefone: dadosExtraidos.telefone.valor,
      whatsapp: dadosExtraidos.whatsapp.valor || dadosExtraidos.telefone.valor,
      email: dadosExtraidos.email.valor,
      data_nascimento: '',
      rua: dadosExtraidos.rua.valor,
      bairro: dadosExtraidos.bairro.valor,
      cep: dadosExtraidos.cep.valor,
      cidade: dadosExtraidos.cidade.valor,
      estado: dadosExtraidos.estado.valor,
      quer_ser_membro: dadosExtraidos.checkboxes.quer_ser_membro,
      deseja_oracao: dadosExtraidos.checkboxes.deseja_oracao,
      quer_conhecer_jesus: dadosExtraidos.checkboxes.quer_conhecer_jesus,
      origem_cadastro: 'card_fisico'
    };

    // Buscar endereço por CEP se disponível
    if (dados.cep && dadosExtraidos.cep.confianca > 70) {
      try {
        const endereco = await buscarEnderecoPorCEP(dados.cep);
        if (endereco) {
          dados.rua = endereco.rua || dados.rua;
          dados.bairro = endereco.bairro || dados.bairro;
          dados.cidade = endereco.cidade || dados.cidade;
          dados.estado = endereco.estado || dados.estado;
          
          // Atualizar confiança com dados do CEP
          confiancas.rua = endereco.rua ? 100 : confiancas.rua;
          confiancas.bairro = endereco.bairro ? 100 : confiancas.bairro;
          confiancas.cidade = endereco.cidade ? 100 : confiancas.cidade;
          confiancas.estado = endereco.estado ? 100 : confiancas.estado;
          setDadosConfianca(confiancas);
        }
      } catch (error) {
        console.error('Erro ao buscar CEP:', error);
      }
    }

    setFormData(dados);
  };

  // Preencher formulário com dados da IA (estruturados)
  const preencherFormularioIA = (dadosIA) => {
    const dados = {
      nome: dadosIA.nome || '',
      sexo: dadosIA.sexo || '',
      telefone: dadosIA.telefone || '',
      whatsapp: dadosIA.whatsapp || dadosIA.telefone || '',
      email: dadosIA.email || '',
      data_nascimento: dadosIA.data_nascimento || '',
      rua: dadosIA.rua || '',
      bairro: dadosIA.bairro || '',
      cep: dadosIA.cep || '',
      cidade: dadosIA.cidade || 'Recife',
      estado: dadosIA.estado || 'PE',
      quer_ser_membro: dadosIA.quer_ser_membro || false,
      deseja_oracao: dadosIA.deseja_oracao || false,
      quer_conhecer_jesus: dadosIA.quer_conhecer_jesus || false,
      origem_cadastro: 'card_fisico'
    };

    // Buscar endereço por CEP se disponível
    if (dados.cep && dados.cep.length === 8) {
      buscarEnderecoPorCEP(dados.cep).then(endereco => {
        if (endereco) {
          setFormData(prev => ({
            ...prev,
            rua: endereco.rua || prev.rua,
            bairro: endereco.bairro || prev.bairro,
            cidade: endereco.cidade || prev.cidade,
            estado: endereco.estado || prev.estado
          }));
        }
      }).catch(error => console.error('Erro ao buscar CEP:', error));
    }

    setFormData(dados);
    
    // Definir confiança alta para dados da IA
    setDadosConfianca({
      nome: 85,
      sexo: 85,
      telefone: 80,
      email: 80,
      cep: 80,
      rua: 75,
      bairro: 75
    });
  };

  const handleInputChange = (event) => {
    const { name, value, checked, type } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSalvarVisitante = async () => {
    // Validações
    if (!formData.nome || !formData.sexo) {
      setError('Nome e sexo são obrigatórios');
      return;
    }

    if (!formData.telefone && !formData.email) {
      setError('Informe pelo menos telefone ou email');
      return;
    }

    setSalvando(true);
    setError('');

    try {
      const response = await api.post('/visitantes/', formData);
      
      setSuccess(`Visitante cadastrado e distribuído com sucesso! ID: ${response.data.id}`);
      setActiveStep(3);
      
      // Resetar formulário após 3 segundos
      setTimeout(() => {
        handleResetForm();
      }, 3000);
    } catch (error) {
      console.error('Erro ao salvar:', error);
      const det = error.response?.data?.detail;
      setError(Array.isArray(det) ? det.map(e => e.msg).join('; ') : det || 'Erro ao cadastrar visitante');
    } finally {
      setSalvando(false);
    }
  };

  const handleResetForm = () => {
    setActiveStep(0);
    setImagemFile(null);
    setImagemPreview('');
    setTextoExtraido('');
    setFormData({
      nome: '',
      sexo: '',
      telefone: '',
      email: '',
      whatsapp: '',
      data_nascimento: '',
      rua: '',
      bairro: '',
      cep: '',
      cidade: '',
      estado: '',
      quer_ser_membro: false,
      deseja_oracao: false,
      quer_conhecer_jesus: false,
      origem_cadastro: 'card_fisico'
    });
    setSuccess('');
    setError('');
  };

  const handleVoltarEtapa = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    }
  };

  return (
    <>
      <Header onLogout={onLogout} />
      <ContextNavBar context="consolidacao" />
      <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 }, px: { xs: 1, md: 2 } }}>
        <ModuleHeader module="Consolidação" title="Capturar Card de Visitante" />

        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {/* Etapa 1: Upload */}
        {activeStep === 0 && (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <CloudUploadIcon sx={{ fontSize: 80, color: 'primary.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Selecione uma foto do card de visitante
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Tire uma foto clara do card ou faça upload de uma imagem existente
            </Typography>
            
            <Box display="flex" gap={2} justifyContent="center" flexWrap="wrap">
              <Button
                variant="contained"
                component="label"
                startIcon={<PhotoCameraIcon />}
                size="large"
              >
                Tirar Foto
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  capture="environment"
                  onChange={handleFileSelect}
                />
              </Button>

              <Button
                variant="outlined"
                component="label"
                startIcon={<CloudUploadIcon />}
                size="large"
              >
                Fazer Upload
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleFileSelect}
                />
              </Button>
            </Box>

            <Box sx={{ mt: 4 }}>
              <Button
                variant="text"
                onClick={() => setActiveStep(2)}
              >
                Ou preencher manualmente
              </Button>
            </Box>
          </Paper>
        )}

        {/* Etapa 2: Processar OCR */}
        {activeStep === 1 && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Imagem do Card
                </Typography>
                {imagemPreview && (
                  <Box sx={{ position: 'relative' }}>
                    <img
                      src={imagemPreview}
                      alt="Preview"
                      style={{
                        width: '100%',
                        maxHeight: '400px',
                        objectFit: 'contain',
                        borderRadius: '8px'
                      }}
                    />
                    <IconButton
                      sx={{ position: 'absolute', top: 8, right: 8 }}
                      onClick={() => {
                        setImagemFile(null);
                        setImagemPreview('');
                        setActiveStep(0);
                      }}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                )}
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, textAlign: 'center', height: '100%' }}>
                {!processandoOCR ? (
                  <>
                    <Typography variant="h6" gutterBottom>
                      Pronto para processar
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      O sistema irá extrair automaticamente os dados do card
                    </Typography>
                    
                    <Button
                      variant="contained"
                      size="large"
                      onClick={handleProcessarOCR}
                      fullWidth
                      sx={{ mb: 2 }}
                    >
                      Processar com OCR
                    </Button>

                    <Button
                      variant="outlined"
                      onClick={handleVoltarEtapa}
                      fullWidth
                    >
                      Trocar Imagem
                    </Button>
                  </>
                ) : (
                  <>
                    <CircularProgress size={60} sx={{ mb: 2 }} />
                    <Typography variant="h6" gutterBottom>
                      Processando imagem...
                    </Typography>
                    <Typography variant="h4" color="primary.main" sx={{ fontSize: '4.4rem', lineHeight: 1 }}>
                      {progressoOCR}%
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                      Aguarde enquanto extraímos os dados do card
                    </Typography>
                  </>
                )}
              </Paper>
            </Grid>
          </Grid>
        )}

        {/* Etapa 3: Revisar Dados */}
        {activeStep === 2 && (
          <Paper sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={3} flexDirection={{ xs: 'column', md: 'row' }} gap={1}>
              <Typography variant="h6" sx={{ fontSize: { xs: '1.2rem', md: '1.5rem' } }}>
                Revisar e Editar Dados
              </Typography>
              {textoExtraido && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={() => setActiveStep(1)}
                >
                  Reprocessar
                </Button>
              )}
            </Box>

            {textoExtraido && (
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Texto extraído do card:
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                  {textoExtraido}
                </Typography>
              </Alert>
            )}

            <Grid container spacing={2}>
              <Grid item xs={12} md={8}>
                <Box display="flex" alignItems="center" gap={1}>
                  <TextField
                    fullWidth
                    label="Nome Completo *"
                    name="nome"
                    value={formData.nome}
                    onChange={handleInputChange}
                    required
                  />
                  {dadosConfianca.nome > 0 && (
                    <Tooltip title={`Confiança: ${dadosConfianca.nome}%`}>
                      <Chip
                        label={`${dadosConfianca.nome}%`}
                        size="small"
                        color={getCorConfianca(dadosConfianca.nome)}
                      />
                    </Tooltip>
                  )}
                </Box>
              </Grid>

              <Grid item xs={12} md={4}>
                <Box display="flex" alignItems="center" gap={1}>
                  <TextField
                    fullWidth
                    select
                    label="Sexo *"
                    name="sexo"
                    value={formData.sexo}
                    onChange={handleInputChange}
                    required
                  >
                    <MenuItem value="masculino">Masculino</MenuItem>
                    <MenuItem value="feminino">Feminino</MenuItem>
                  </TextField>
                  {dadosConfianca.sexo > 0 && (
                    <Tooltip title={`Confiança: ${dadosConfianca.sexo}%`}>
                      <Chip
                        label={`${dadosConfianca.sexo}%`}
                        size="small"
                        color={getCorConfianca(dadosConfianca.sexo)}
                      />
                    </Tooltip>
                  )}
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box display="flex" alignItems="center" gap={1}>
                  <TextField
                    fullWidth
                    label="Telefone"
                    name="telefone"
                    value={formData.telefone}
                    onChange={handleInputChange}
                  />
                  {dadosConfianca.telefone > 0 && (
                    <Tooltip title={`Confiança: ${dadosConfianca.telefone}%`}>
                      <Chip
                        label={`${dadosConfianca.telefone}%`}
                        size="small"
                        color={getCorConfianca(dadosConfianca.telefone)}
                      />
                    </Tooltip>
                  )}
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box display="flex" alignItems="center" gap={1}>
                  <TextField
                    fullWidth
                    label="WhatsApp"
                    name="whatsapp"
                    value={formData.whatsapp}
                    onChange={handleInputChange}
                  />
                  {dadosConfianca.whatsapp > 0 && (
                    <Tooltip title={`Confiança: ${dadosConfianca.whatsapp}%`}>
                      <Chip
                        label={`${dadosConfianca.whatsapp}%`}
                        size="small"
                        color={getCorConfianca(dadosConfianca.whatsapp)}
                      />
                    </Tooltip>
                  )}
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box display="flex" alignItems="center" gap={1}>
                  <TextField
                    fullWidth
                    label="Email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                  />
                  {dadosConfianca.email > 0 && (
                    <Tooltip title={`Confiança: ${dadosConfianca.email}%`}>
                      <Chip
                        label={`${dadosConfianca.email}%`}
                        size="small"
                        color={getCorConfianca(dadosConfianca.email)}
                      />
                    </Tooltip>
                  )}
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Data de Nascimento"
                  name="data_nascimento"
                  type="date"
                  value={formData.data_nascimento}
                  onChange={handleInputChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Rua/Endereço"
                  name="rua"
                  value={formData.rua}
                  onChange={handleInputChange}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Box display="flex" alignItems="center" gap={1}>
                  <TextField
                    fullWidth
                    label="Bairro"
                    name="bairro"
                    value={formData.bairro}
                    onChange={handleInputChange}
                  />
                  {dadosConfianca.bairro > 0 && (
                    <Tooltip title={`Confiança: ${dadosConfianca.bairro}%`}>
                      <Chip
                        label={`${dadosConfianca.bairro}%`}
                        size="small"
                        color={getCorConfianca(dadosConfianca.bairro)}
                      />
                    </Tooltip>
                  )}
                </Box>
              </Grid>

              <Grid item xs={12} md={4}>
                <Box display="flex" alignItems="center" gap={1}>
                  <TextField
                    fullWidth
                    label="CEP"
                    name="cep"
                    value={formData.cep}
                    onChange={handleInputChange}
                  />
                  {dadosConfianca.cep > 0 && (
                    <Tooltip title={`Confiança: ${dadosConfianca.cep}%`}>
                      <Chip
                        label={`${dadosConfianca.cep}%`}
                        size="small"
                        color={getCorConfianca(dadosConfianca.cep)}
                      />
                    </Tooltip>
                  )}
                </Box>
              </Grid>

              <Grid item xs={12} md={4}>
                <Box display="flex" alignItems="center" gap={1}>
                  <TextField
                    fullWidth
                    label="Cidade"
                    name="cidade"
                    value={formData.cidade}
                    onChange={handleInputChange}
                  />
                  {dadosConfianca.cidade > 0 && (
                    <Tooltip title={`Confiança: ${dadosConfianca.cidade}%`}>
                      <Chip
                        label={`${dadosConfianca.cidade}%`}
                        size="small"
                        color={getCorConfianca(dadosConfianca.cidade)}
                      />
                    </Tooltip>
                  )}
                </Box>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Estado"
                  name="estado"
                  value={formData.estado}
                  onChange={handleInputChange}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                  Motivos da Visita:
                </Typography>
              </Grid>

              <Grid item xs={12} md={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.quer_ser_membro}
                      onChange={handleInputChange}
                      name="quer_ser_membro"
                    />
                  }
                  label="Quer ser membro"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.deseja_oracao}
                      onChange={handleInputChange}
                      name="deseja_oracao"
                    />
                  }
                  label="Deseja oração"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.quer_conhecer_jesus}
                      onChange={handleInputChange}
                      name="quer_conhecer_jesus"
                    />
                  }
                  label="Quer conhecer Jesus"
                />
              </Grid>
            </Grid>

            <Box display="flex" gap={2} justifyContent="flex-end" mt={3} flexDirection={{ xs: 'column', md: 'row' }}>
              <Button
                variant="outlined"
                onClick={handleVoltarEtapa}
                disabled={salvando}
              >
                Voltar
              </Button>
              <Button
                variant="contained"
                onClick={handleSalvarVisitante}
                disabled={salvando}
                startIcon={salvando ? <CircularProgress size={20} /> : <CheckCircleIcon />}
              >
                {salvando ? 'Salvando...' : 'Salvar e Distribuir'}
              </Button>
            </Box>
          </Paper>
        )}

        {/* Etapa 4: Sucesso */}
        {activeStep === 3 && (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom color="success.main">
              Card processado com sucesso!
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              O visitante foi cadastrado e distribuído automaticamente para um consolidador
            </Typography>
            
            <Button
              variant="contained"
              size="large"
              onClick={handleResetForm}
            >
              Cadastrar Novo Card
            </Button>
          </Paper>
        )}
      </Container>
      <Footer />
    </>
  );
};

export default CapturarCard;
